import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";

import { useDriverNotes } from "../../hooks/queries";
import { supabase } from "../../supabaseClient";
import { useCreateDriverNote, useUpdateDriverNote } from "../../hooks/mutation";
import { toast } from "react-toastify";
import { FaAngleLeft, FaAngleRight, FaEdit } from "react-icons/fa";
import { PAGE_SIZE } from "../Utils/constant";

const DriverNewNote = () => {
  const [page, setPage] = useState(1);

  const [isEdit, setIsEdit] = useState(false);
  const [driverNote, setDriverNote] = useState(null);
  const [search, setSearch] = useState(null);
  const { data } = useDriverNotes(isEdit, search, page);
  const [driverNotesData, setDriverNotesData] = useState();
  const [totalCount, setTotalCount] = useState(0);
  useEffect(() => {
    if (data) {
      setDriverNotesData(data.data);
      setTotalCount(data.count);
    }
  }, [data]);

  const [drivers, setDrivers] = useState([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      driverID: "",
      memo: "",
    },
  });

  useEffect(() => {
    const fetchDrivers = async () => {
      const { data: Drivers, error: dError } = await supabase
        .from("driver_auth")
        .select(
          `
          id,
          email,
          is_active,
          driver_profiles (
            id,
            first_name,
            last_name
          )
        `
        )
        .eq("is_active", true);

      if (!dError) {
        // Transform data to match expected format
        const transformedDrivers =
          Drivers?.map((driver) => ({
            DriverID: driver.id,
            FirstName: driver.driver_profiles?.[0]?.first_name || "",
            LastName: driver.driver_profiles?.[0]?.last_name || "",
            Email: driver.email,
          })) || [];

        setDrivers(transformedDrivers);
      }
    };
    fetchDrivers();
  }, []);

  useEffect(() => {
    if (driverNote) {
      reset({
        driverID: driverNote.Driver.DriverID,
        memo: driverNote.Memo,
      });
    }
  }, [driverNote, reset]);

  const onNextPage = useCallback(() => {
    setPage((old) => old + 1);
  }, []);
  const onPrevPage = useCallback(() => {
    console.log(page);
    setPage((old) => old - 1);
  }, []);

  const onSuccess = useCallback((_data) => {
    toast.success("Success");
  }, []);
  const onError = useCallback(
    (err) => {
      console.log("error", err);
      reset();
    },
    [reset]
  );

  const onAddNew = () => {
    setIsEdit(true);
    reset({
      driverID: drivers[0].DriverID,
      memo: "",
    });
  };

  const OnCancel = () => {
    setIsEdit(false);
    setDriverNote();
  };

  const { mutate: createManager, isPending: isCreating } = useCreateDriverNote(
    onSuccess,
    onError
  );

  const { mutate: updateDriverNote, isPending: isUpdating } =
    useUpdateDriverNote(onSuccess, onError);

  const OnCreate = useCallback(
    (data) => {
      if (driverNote) {
        updateDriverNote({ ...data, ID: driverNote.ID });
      } else {
        createManager(data);
      }
    },
    [createManager, driverNote, updateDriverNote]
  );
  const OnSearch = useCallback(
    (data) => {
      setSearch(data);
    },
    [setSearch]
  );

  return (
    <>
      {isEdit ? (
        <div className="driver-patent-edit-content mt-4 rounded-lg">
          <div className="flex justify-between mb-2">
            <div>Driver</div>
            <div className="w-35">
              <Controller
                control={control}
                name="driverID"
                rules={{
                  required: "Required",
                }}
                render={({ field: { value, onChange } }) => (
                  <select
                    className="custom-input rounded-lg"
                    value={value}
                    onChange={onChange}
                    error={errors.driverID?.message}
                  >
                    {drivers.map((driver) => (
                      <option key={driver.DriverID} value={driver.DriverID}>
                        {driver.DriverCode}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>Memo</div>
            <div className="flex justify-between w-35 items-end">
              <Controller
                control={control}
                name="memo"
                rules={{
                  required: "Required",
                }}
                render={({ field: { value, onChange } }) => (
                  <textarea
                    className="custom-input w-100 h-20"
                    value={value}
                    onChange={onChange}
                    placeholder="Add any important notes or special instructions here.."
                  ></textarea>
                )}
              />
              <div>{errors.memo?.message}</div>
            </div>
          </div>
          <div className="flex gap-4 mt-4 save-button">
            <button
              disabled={isCreating | isUpdating}
              onClick={handleSubmit(OnCreate)}
              className="justify-between p-2 pr-20 pl-20 rounded-lg mr-1 flex items-center custom-blue-button"
            >
              <span>{driverNote ? "Update" : "Create"}</span>
            </button>
            <div>
              <div
                onClick={OnCancel}
                className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button"
              >
                <span>Cancel</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex ">
            <h5 className="mb-0">Driver Notes</h5>
            <button
              className="justify-between rounded-lg ml-1 flex items-center custom-blue-button border-none"
              onClick={onAddNew}
            >
              <span>Add New</span>
            </button>
          </div>
          <SearchBar setSearch={setSearch} OnSearch={OnSearch} />

          <table className="table-auto w-full mt-4 border border-gray-200">
            <thead>
              <tr className="bg-gray-100 cursor-pointer">
                <th className="px-4 py-2 border">ID</th>
                <th className="px-4 py-2 border">DriverCode</th>
                <th className="px-4 py-2 border">Memo</th>
                <th className="px-4 py-2 border">Edit</th>
              </tr>
            </thead>
            <tbody>
              {driverNotesData &&
                driverNotesData.map((driverNote) => (
                  <tr key={driverNote.ID} className="hover:bg-gray-50">
                    <td className="px-4 py-2 border">{driverNote.ID}</td>
                    <td className="px-4 py-2 border">
                      {driverNote.Driver.DriverCode}
                    </td>
                    <td className="px-4 py-2 border">{driverNote.Memo}</td>
                    <td className="px-4 py-2 border cursor-point">
                      <FaEdit
                        onClick={() => {
                          setDriverNote(driverNote);
                          setIsEdit(true);
                        }}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          <div className="pagination-container">
            <div className="pagination-content">
              <div>
                <p className="pagination-text">
                  Showing <span>{(page - 1) * PAGE_SIZE + 1}</span> to{" "}
                  <span>{Math.min(totalCount, page * PAGE_SIZE)}</span> of{" "}
                  <span>{totalCount}</span> results
                </p>
              </div>
              <div>
                <nav className="pagination-nav">
                  <button
                    onClick={onPrevPage}
                    disabled={page == 1}
                    className={`pagination-item ${page == 1 ? "disabled" : ""}`}
                  >
                    <FaAngleLeft className="pagination-icon" />
                  </button>
                  <button
                    onClick={onNextPage}
                    disabled={page * PAGE_SIZE > totalCount}
                    className={`pagination-item ${
                      page * PAGE_SIZE > totalCount ? "disabled" : ""
                    }`}
                  >
                    <FaAngleRight className="pagination-icon" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DriverNewNote;

const SearchBar = ({ setSearch, OnSearch }) => {
  const {
    control: searchControl,
    handleSubmit: handleSearchSubmit,
    reset: searchReset,
  } = useForm({
    defaultValues: {
      type: "ID",
      which: "contains",
      text: "",
    },
  });

  return (
    <div className="flex mt-4 items-center gap-4 condition-bar">
      <div className="flex items-center">
        <p className="mb-0">Look For:</p>
        <div className="flex justify-between w-10 ml-1">
          <Controller
            control={searchControl}
            name="type"
            render={({ field: { value, onChange } }) => (
              <select
                className="custom-input rounded-lg cursor-pointer"
                value={value}
                onChange={onChange}
              >
                <option value={"ID"}>DriverNote ID</option>
                <option value={"DriverCode"}>DriverCode</option>
                <option value={"Memo"}>Memo</option>
              </select>
            )}
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <p className="mb-0">Which:</p>
        <div className="flex justify-between w-10 ml-1">
          <Controller
            control={searchControl}
            name="which"
            render={({ field: { value, onChange } }) => (
              <select
                className="custom-input rounded-lg cursor-pointer"
                value={value}
                onChange={onChange}
              >
                <option value="contains">contains</option>
                <option value="starts with">starts with</option>
                <option value="equals">equals</option>
                <option value="ends with">ends with</option>
              </select>
            )}
          />
        </div>
        <div className="">
          <Controller
            control={searchControl}
            name="text"
            rules={{
              required: "Required",
            }}
            render={({ field: { value, onChange } }) => (
              <input
                type=""
                value={value}
                onChange={onChange}
                className="custom-input rounded-lg w-100 "
              />
            )}
          />
        </div>
      </div>
      <div className="flex">
        <div>
          <button
            onClick={handleSearchSubmit(OnSearch)}
            className="justify-between rounded-lg flex items-center w-30 custom-search-button border-none"
          >
            <span>Search</span>
          </button>
        </div>
        <div>
          <button
            onClick={() => {
              searchReset({
                type: "ID",
                which: "contains",
                text: "",
              });
              setSearch(null);
            }}
            className="justify-between rounded-lg ml-1 flex items-center custom-reset-button"
          >
            <span>Reset</span>
          </button>
        </div>
      </div>
    </div>
  );
};
